import { oc } from "@orpc/contract";
import { z } from "zod";

// --- Shared Schemas ---

export const SemanticModelNameSchema = z.enum([
  "fast",
  "balanced",
  "smart",
  "reasoning",
]);

export const AgentStopTypeSchema = z.enum(["MAX_STEPS", "NO_MORE_TASKS"]);

export const AgentModelConfigSchema = z.object({
  model: SemanticModelNameSchema.optional(),
  maxSteps: z.number().min(1).max(1000).optional(),
  stopType: AgentStopTypeSchema.optional(),
});

export const AgentInputFieldDefSchema = z.object({
  fieldName: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  socketType: z.string(),
  required: z.boolean(),
  sortOrder: z.number(),
});

export const AgentOutputFieldDefSchema = z.object({
  fieldName: z.string(),
  displayName: z.string(),
  description: z.string(),
  socketType: z.string(),
  sortOrder: z.number(),
});

export const AgentToolDefSchema = z.object({
  toolType: z.string(),
  config: z.record(z.string(), z.unknown()).optional(),
});

export const ToolCategorySchema = z.enum([
  "Email",
  "AP Invoices",
  "Sales Orders",
  "Purchase Orders",
  "Matching",
  "Customers",
  "Payments",
  "Utility",
  "Tasks",
  "Remote Desktop",
  "AI & Agents",
  "Data Access",
  "Integration",
  "Ship & Debit",
]);

export const ToolPermissionSchema = z.object({
  action: z.string(),
  subject: z.string(),
});

export const CatalogToolMetadataSchema = z.object({
  type: z.string(),
  name: z.string(),
  description: z.string(),
  category: ToolCategorySchema,
  readOnly: z.boolean(),
  lifecycleManaged: z.boolean().optional(),
  requiredPermissions: z.array(ToolPermissionSchema).optional(),
  deprecated: z
    .object({
      reason: z.string(),
      replacedBy: z.string().optional(),
    })
    .optional(),
  visibility: z
    .object({
      visibility: z.enum(["visible", "hidden", "restricted", "disabled"]),
      predicateResults: z
        .array(
          z.array(
            z.object({
              type: z.string(),
              passed: z.boolean(),
              detail: z.string(),
            }),
          ),
        )
        .optional(),
      disabledReason: z.string().optional(),
    })
    .optional(),
});

// --- Agent Run Schemas ---

export const AgentRunStatusSchema = z.enum(["running", "completed", "failed"]);

export const AgentRunMetaSchema = z.object({
  confidence: z.number(),
  reasoning: z.string(),
  unresolvableFields: z.array(
    z.object({
      field: z.string(),
      reason: z.string(),
    }),
  ),
});

export const AgentRunSummarySchema = z.object({
  id: z.uuid(),
  agentDefinitionId: z.uuid().nullable(),
  status: AgentRunStatusSchema,
  model: z.string().nullable().optional(),
  llmSteps: z.number().nullable().optional(),
  totalInputTokens: z.number().nullable().optional(),
  totalOutputTokens: z.number().nullable().optional(),
  totalReasoningTokens: z.number().nullable().optional(),
  totalCacheReadTokens: z.number().nullable().optional(),
  totalCacheWriteTokens: z.number().nullable().optional(),
  finishReason: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  startedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable().optional(),
  createdAt: z.iso.datetime(),
});

export const AgentMessageResponseSchema = z.object({
  id: z.uuid(),
  agentRunId: z.uuid(),
  stepNumber: z.number().nullable().optional(),
  role: z.enum(["system", "user", "assistant", "tool"]),
  text: z.string().nullable().optional(),
  reasoning: z.string().nullable().optional(),
  finishReason: z.string().nullable().optional(),
  inputTokens: z.number().nullable().optional(),
  outputTokens: z.number().nullable().optional(),
  reasoningTokens: z.number().nullable().optional(),
  cacheReadTokens: z.number().nullable().optional(),
  cacheWriteTokens: z.number().nullable().optional(),
  createdAt: z.iso.datetime(),
});

export const ToolCallRunResponseSchema = z.object({
  id: z.uuid(),
  agentRunId: z.uuid(),
  agentMessageId: z.uuid().nullable().optional(),
  toolType: z.string().nullable().optional(),
  toolCallId: z.string().nullable().optional(),
  status: z.string(),
  inputData: z.record(z.string(), z.unknown()),
  outputData: z.record(z.string(), z.unknown()).nullable().optional(),
  screenshotDocumentId: z.uuid().nullable().optional(),
  screenshotSignedUrl: z.string().nullable().optional(),
  startedAt: z.iso.datetime().nullable().optional(),
  completedAt: z.iso.datetime().nullable().optional(),
  duration: z.number().nullable().optional(),
  stopReason: z.string().nullable().optional(),
  /**
   * When this tool call is a task-loop phase that spawned a child agent run,
   * the child run's id. The trace nests the child run's timeline beneath it.
   */
  childAgentRunId: z.uuid().nullable().optional(),
  /**
   * When this tool call is a task-loop phase that launched a setup/teardown
   * workflow, the child WorkflowRun id (drill-in to the workflow trace).
   */
  childWorkflowRunId: z.uuid().nullable().optional(),
  /**
   * Workflow-step provenance for tool calls that ran inside a workflow step
   * (e.g. computer-use operator iterations from a remote-desktop step). The
   * workflow run the iteration belongs to, and the StepRun it nests under.
   * Null for plain agent tool calls. Lets a workflow run's trace render the
   * iteration under the step it ran inside, while `agentRunId` still
   * attributes it to the orchestrating agent run.
   */
  workflowRunId: z.uuid().nullable().optional(),
  parentStepRunId: z.uuid().nullable().optional(),
});

// --- Response Schemas ---

export const AgentDefinitionResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  createdBy: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  templateType: z.string().nullable().optional(),
  systemPrompt: z.string(),
  modelConfig: AgentModelConfigSchema,
  config: z.record(z.string(), z.unknown()),
  inputFields: z.array(AgentInputFieldDefSchema),
  outputFields: z.array(AgentOutputFieldDefSchema),
  tools: z.array(AgentToolDefSchema),
  isActive: z.boolean(),
  version: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const AgentDefinitionSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  templateType: z.string().nullable().optional(),
  isActive: z.boolean(),
  version: z.number(),
  toolCount: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const AgentTemplateResponseSchema = z.object({
  templateType: z.string(),
  name: z.string(),
  description: z.string(),
  requiredTools: z.array(z.string()),
  availableTools: z.array(z.string()),
  defaultInputFields: z.array(AgentInputFieldDefSchema),
  defaultOutputFields: z.array(AgentOutputFieldDefSchema),
  defaultModelConfig: AgentModelConfigSchema,
});

// --- Request Schemas ---

export const CreateAgentDefinitionRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  templateType: z.string().optional(),
  systemPrompt: z.string().optional(),
  modelConfig: AgentModelConfigSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  inputFields: z.array(AgentInputFieldDefSchema).optional(),
  outputFields: z.array(AgentOutputFieldDefSchema).optional(),
  tools: z.array(AgentToolDefSchema).optional(),
  isActive: z.boolean().optional(),
});

export const UpdateAgentDefinitionRequestSchema = z.object({
  agentDefinitionId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  systemPrompt: z.string().optional(),
  modelConfig: AgentModelConfigSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  inputFields: z.array(AgentInputFieldDefSchema).optional(),
  outputFields: z.array(AgentOutputFieldDefSchema).optional(),
  tools: z.array(AgentToolDefSchema).optional(),
  isActive: z.boolean().optional(),
});

// --- Contracts ---

export const agentDefinitionContracts = {
  list: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(
      z.object({
        definitions: z.array(AgentDefinitionSummarySchema),
        total: z.number(),
      }),
    )
    .route({ method: "GET" }),

  get: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(AgentDefinitionResponseSchema)
    .route({ method: "GET" }),

  create: oc
    .input(CreateAgentDefinitionRequestSchema)
    .output(AgentDefinitionResponseSchema)
    .route({ method: "POST" }),

  update: oc
    .input(UpdateAgentDefinitionRequestSchema)
    .output(AgentDefinitionResponseSchema)
    .route({ method: "PUT" }),

  delete: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  listToolCatalog: oc
    .input(z.object({}))
    .output(
      z.object({
        tools: z.array(CatalogToolMetadataSchema),
      }),
    )
    .route({ method: "GET" }),

  listTemplates: oc
    .input(z.object({}))
    .output(
      z.object({
        templates: z.array(AgentTemplateResponseSchema),
      }),
    )
    .route({ method: "GET" }),

  listRuns: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(
      z.object({
        runs: z.array(AgentRunSummarySchema),
        total: z.number(),
      }),
    )
    .route({ method: "GET" }),

  getRunTimeline: oc
    .input(z.object({ agentRunId: z.uuid() }))
    .output(
      z.object({
        run: AgentRunSummarySchema,
        messages: z.array(AgentMessageResponseSchema),
        toolCalls: z.array(ToolCallRunResponseSchema),
        // Screenshot-bearing action tool calls for this run AND its descendant
        // child runs, ordered by startedAt. Task-loop children audit their
        // remote-desktop actions under their own run id while the recording is
        // bound to the parent loop run, so the recording timeline markers and
        // screenshot gallery would be empty if they only read `toolCalls`
        // (which holds just this run's own steps, for the trace view).
        recordingToolCalls: z.array(ToolCallRunResponseSchema),
        recordingDocumentId: z.string().uuid().nullable(),
        // The screenshot captured at the start of the run (before the first
        // action). Lets the UI render each click on the frame the agent saw.
        initialScreenshotDocumentId: z.string().uuid().nullable(),
        initialScreenshotSignedUrl: z.string().nullable(),
      }),
    )
    .route({ method: "GET" }),

  cancelRun: oc
    .input(z.object({ agentRunId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),

  startRun: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        inputData: z.record(z.string(), z.unknown()).optional().default({}),
      }),
    )
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),
} as const;

// --- Export types ---

export type AgentDefinitionResponse = z.infer<
  typeof AgentDefinitionResponseSchema
>;
export type AgentDefinitionSummary = z.infer<
  typeof AgentDefinitionSummarySchema
>;
export type AgentTemplateResponse = z.infer<typeof AgentTemplateResponseSchema>;
export type CreateAgentDefinitionRequest = z.infer<
  typeof CreateAgentDefinitionRequestSchema
>;
export type UpdateAgentDefinitionRequest = z.infer<
  typeof UpdateAgentDefinitionRequestSchema
>;
export type CatalogToolMetadata = z.infer<typeof CatalogToolMetadataSchema>;
export type AgentRunSummary = z.infer<typeof AgentRunSummarySchema>;
export type AgentMessageResponse = z.infer<typeof AgentMessageResponseSchema>;
export type ToolCallRunResponse = z.infer<typeof ToolCallRunResponseSchema>;
